import React, { useMemo, useState } from 'react';
import './PaymentSettings.css';
import { fetchPaymentSettings, updatePaymentSettings } from '../api/paymentSettingsApi';
import {
    TenantPaymentConfigField,
    TenantPaymentFieldType,
    TenantPaymentSetting,
    TenantPaymentSettingsUpdatePayload,
} from '../types/payment';

interface PaymentFieldDraft extends TenantPaymentConfigField {
    value: string;
    error?: string;
}

interface PaymentSettingDraft extends Omit<TenantPaymentSetting, 'fields'> {
    fields: PaymentFieldDraft[];
    markVerified: boolean;
}

export default function PaymentSettingsPage() {
    const [pinInput, setPinInput] = useState('');
    const [activePin, setActivePin] = useState<string | null>(null);
    const [settings, setSettings] = useState<PaymentSettingDraft[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const hasUnlocked = useMemo(() => Boolean(activePin), [activePin]);

    const handleUnlock = async () => {
        setError(null);
        setSuccess(null);

        if (!pinInput || pinInput.trim().length < 4) {
            setError('Inserisci un PIN valido (almeno 4 cifre).');
            return;
        }

        setLoading(true);
        try {
            const response = await fetchPaymentSettings(pinInput.trim());
            const drafts: PaymentSettingDraft[] = response.settings.map(setting => ({
                ...setting,
                fields: (setting.fields ?? []).map(field => ({
                    ...field,
                    value: field.value ?? '',
                    error: undefined,
                })),
                markVerified: false,
            }));
            setSettings(drafts);
            setActivePin(pinInput.trim());
            setPinInput('');
            setSuccess('Configurazioni caricate. Ricorda di salvare dopo eventuali modifiche.');
        } catch (apiError) {
            const message = extractErrorMessage(apiError);
            setError(message || 'Impossibile recuperare le impostazioni.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = (provider: string) => {
        setSettings(prev => prev.map(item =>
            item.provider === provider ? { ...item, active: !item.active } : item
        ));
    };

    const handleDisplayNameChange = (provider: string, value: string) => {
        setSettings(prev => prev.map(item =>
            item.provider === provider ? { ...item, displayName: value } : item
        ));
    };

    const handleFieldChange = (provider: string, fieldKey: string, value: string) => {
        setSettings(prev => prev.map(item => {
            if (item.provider !== provider) {
                return item;
            }
            const nextFields = item.fields.map(field =>
                field.key === fieldKey ? { ...field, value, error: undefined } : field
            );
            return { ...item, fields: nextFields };
        }));
    };

    const handleToggleVerified = (provider: string) => {
        setSettings(prev => prev.map(item =>
            item.provider === provider ? { ...item, markVerified: !item.markVerified } : item
        ));
    };

    const handleSave = async () => {
        setError(null);
        setSuccess(null);

        if (!activePin) {
            setError('Inserisci e conferma prima il PIN.');
            return;
        }

        if (!settings.length) {
            setError('Nessuna impostazione da salvare.');
            return;
        }

        let hasErrors = false;
        const drafts: PaymentSettingDraft[] = settings.map(setting => ({
            ...setting,
            fields: setting.fields.map(field => ({ ...field, error: undefined })),
        }));

        const payload: TenantPaymentSettingsUpdatePayload = { settings: [] };

        drafts.forEach((setting, settingIndex) => {
            const isActive = setting.active;
            const fieldPayload = setting.fields.map((field, fieldIndex) => {
                const rawValue = field.value ?? '';
                const trimmedValue = trimForValidation(rawValue, field.type);
                let error: string | undefined;

                if (isActive && field.required && !trimmedValue) {
                    error = 'Campo obbligatorio';
                }
                if (!error && field.maxLength && trimmedValue.length > field.maxLength) {
                    error = `Max ${field.maxLength} caratteri`;
                }

                if (error) {
                    hasErrors = true;
                }

                drafts[settingIndex].fields[fieldIndex] = { ...field, error, value: rawValue };

                return {
                    key: field.key,
                    value: trimmedValue ? normalisePayloadValue(rawValue, field.type) : null,
                };
            });

            payload.settings.push({
                provider: setting.provider,
                displayName: setting.displayName?.trim() || undefined,
                active: setting.active,
                fields: fieldPayload,
                markVerified: setting.markVerified || undefined,
            });
        });

        if (hasErrors) {
            setSettings(drafts);
            setError('Compila o correggi i campi obbligatori evidenziati.');
            return;
        }

        setSettings(drafts);
        setSaving(true);
        try {
            const response = await updatePaymentSettings(activePin, payload);
            const normalized = response.settings.map(item => ({
                ...item,
                fields: (item.fields ?? []).map(field => ({
                    ...field,
                    value: field.value ?? '',
                    error: undefined,
                })),
                markVerified: false,
            }));
            setSettings(normalized);
            setSuccess('Impostazioni salvate correttamente.');
        } catch (apiError) {
            const message = extractErrorMessage(apiError);
            setError(message || 'Errore durante il salvataggio.');
        } finally {
            setSaving(false);
        }
    };

    const sortedSettings = useMemo(() => {
        return [...settings].sort((a, b) => a.provider.localeCompare(b.provider));
    }, [settings]);

    return (
        <div className="payment-settings-page">
            <header className="payment-settings-header">
                <h1>Credenziali Pagamento</h1>
                <p>Le credenziali sono cifrate e protette da PIN. Inserisci il PIN per visualizzarle e modificarle.</p>
            </header>

            <section className="pin-section">
                <label htmlFor="pin-input">PIN di sicurezza</label>
                <div className="pin-actions">
                    <input
                        id="pin-input"
                        type="password"
                        value={pinInput}
                        onChange={event => setPinInput(event.target.value)}
                        placeholder="******"
                        disabled={loading}
                    />
                    <button onClick={handleUnlock} disabled={loading}>
                        {loading ? 'Verifica…' : 'Sblocca'}
                    </button>
                </div>
                {activePin && <small className="pin-success">PIN verificato. Le modifiche richiedono un nuovo salvataggio.</small>}
            </section>

            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            <section className="settings-actions">
                <button className="primary" onClick={handleSave} disabled={!hasUnlocked || saving}>
                    {saving ? 'Salvataggio…' : 'Salva modifiche'}
                </button>
            </section>

            {!hasUnlocked && (
                <div className="placeholder">
                    <p>Inserisci il PIN per vedere i provider configurati.</p>
                </div>
            )}

            {hasUnlocked && (
                <div className="settings-grid">
                    {sortedSettings.map((setting, index) => (
                        <article key={setting.provider} className={`provider-card ${setting.active ? 'active' : 'inactive'}`}>
                            <header className="provider-header">
                                <div>
                                    <h2>{setting.provider}</h2>
                                    <p>{setting.displayName || 'Nome visualizzato non impostato'}</p>
                                    {setting.requiresConfig && <span className="provider-chip required">Configurazione obbligatoria</span>}
                                </div>
                                <div className="provider-switch">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={setting.active}
                                            onChange={() => handleToggleActive(setting.provider)}
                                        />
                                        <span>Attivo</span>
                                    </label>
                                </div>
                            </header>

                            <div className="provider-body">
                                <label>Nome pubblico</label>
                                <input
                                    type="text"
                                    value={setting.displayName ?? ''}
                                    onChange={event => handleDisplayNameChange(setting.provider, event.target.value)}
                                    placeholder="Es. Stripe principale"
                                />

                                {setting.fields.length > 0 ? (
                                    <div className="field-grid">
                                        {setting.fields.map((field) => (
                                            <div className="field-group" key={field.key}>
                                                <label htmlFor={`${setting.provider}-${field.key}`}>
                                                    {field.label}
                                                    {field.required && <span className="required-asterisk">*</span>}
                                                </label>
                                                {renderFieldInput(field, value => handleFieldChange(setting.provider, field.key, value))}
                                                {field.helperText && <small className="field-helper">{field.helperText}</small>}
                                                {field.error && <small className="field-error">{field.error}</small>}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="field-fallback">
                                        <p>Non ci sono campi configurabili per questo provider.</p>
                                    </div>
                                )}

                                {setting.maskPreview && Object.keys(setting.maskPreview).length > 0 && (
                                    <div className="mask-preview">
                                        <strong>Anteprima mascherata</strong>
                                        <ul>
                                            {Object.entries(setting.maskPreview ?? {}).map(([key, value]) => (
                                                <li key={key}><span>{key}</span><code>{value}</code></li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="verify-toggle">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={setting.markVerified}
                                            onChange={() => handleToggleVerified(setting.provider)}
                                        />
                                        <span>Ho verificato queste credenziali</span>
                                    </label>
                                </div>

                                <footer className="provider-footer">
                                    <small>
                                        Ultima verifica: {setting.lastVerifiedAt ? new Date(setting.lastVerifiedAt).toLocaleString() : '—'}
                                        {setting.lastVerifiedBy ? ` da ${setting.lastVerifiedBy}` : ''}
                                    </small>
                                    <small>
                                        Ultimo aggiornamento: {setting.updatedAt ? new Date(setting.updatedAt).toLocaleString() : '—'}
                                        {setting.updatedBy ? ` da ${setting.updatedBy}` : ''}
                                    </small>
                                </footer>
                            </div>
                        </article>
                    ))}

                    {sortedSettings.length === 0 && (
                        <div className="placeholder">
                            <p>Nessun provider configurato. Usa "Aggiungi provider" per iniziare.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function extractErrorMessage(error: unknown): string | null {
    if (!error) return null;
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    return null;
}

function trimForValidation(value: string, type: TenantPaymentFieldType): string {
    if (!value) {
        return '';
    }
    if (type === 'TEXTAREA') {
        return value.trim();
    }
    return value.trim();
}

function normalisePayloadValue(value: string, type: TenantPaymentFieldType): string {
    if (!value) {
        return '';
    }
    if (type === 'TEXTAREA') {
        return value.trim();
    }
    return value.trim();
}

function renderFieldInput(field: PaymentFieldDraft, onChange: (value: string) => void) {
    const inputId = `${field.key}-${field.type}`;
    if (field.type === 'TEXTAREA') {
        return (
            <textarea
                id={inputId}
                className={field.error ? 'error' : ''}
                value={field.value}
                onChange={event => onChange(event.target.value)}
                rows={4}
                placeholder={field.placeholder ?? ''}
            />
        );
    }
    return (
        <input
            id={inputId}
            type={field.type === 'SECRET' ? 'password' : 'text'}
            className={field.error ? 'error' : ''}
            value={field.value}
            onChange={event => onChange(event.target.value)}
            placeholder={field.placeholder ?? ''}
        />
    );
}
